import React, { useEffect, useState } from "react";
import { Sliders, ListChecks, Plus, X, Download, Loader2, ArrowRight, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Cell, PieChart, Pie, ResponsiveContainer, Tooltip, LabelList } from "recharts";
import { fmtCurrency, fmtValue } from "../lib/numbersCalc";
import { publicApi } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

const has = (x) => x !== null && x !== undefined && !Number.isNaN(x);

// ─────────────────────────── Result charts ───────────────────────────
export function Charts({ results }) {
  const v = results._inputs || {};
  const gp = results.gross_profit?.status === "ok" ? results.gross_profit.value : (has(v.turnover) && has(v.direct_costs) ? v.turnover - v.direct_costs : null);
  const be = results.breakeven_annual?.status === "ok" ? results.breakeven_annual.value : null;

  const breakdown = [
    { name: "Revenue", value: v.turnover, fill: "#0f172a" },
    { name: "Direct costs", value: v.direct_costs, fill: "#64748b" },
    { name: "Gross profit", value: gp, fill: "#14b8a6" },
    { name: "Overheads", value: v.total_overheads, fill: "#64748b" },
    { name: "Net profit", value: v.net_profit, fill: "#f97316" },
  ].filter((d) => has(d.value));

  const beData = has(v.turnover) && has(be) && be > 0 ? [
    { name: "Break-even", value: be, fill: "#64748b" },
    { name: "Your revenue", value: v.turnover, fill: v.turnover >= be ? "#14b8a6" : "#ef4444" },
  ] : null;

  const conc = has(v.turnover) && has(v.largest_customer_revenue) && v.turnover > 0 ? [
    { name: "Largest customer", value: v.largest_customer_revenue, fill: "#f97316" },
    { name: "All other customers", value: Math.max(0, v.turnover - v.largest_customer_revenue), fill: "#0f172a" },
  ] : null;

  if (breakdown.length < 2 && !beData && !conc) return null;
  const concPct = conc ? Math.round((conc[0].value / (conc[0].value + conc[1].value)) * 100) : 0;
  const headroom = beData ? v.turnover - be : 0;

  const tip = { contentStyle: { fontSize: 12, borderRadius: 12, border: "1px solid #e2e8f0" }, formatter: (val) => [fmtCurrency(val), ""] };

  return (
    <div data-testid="calc-charts">
      <h2 className="font-display font-extrabold text-navy text-lg flex items-center gap-2 mb-4"><BarChart3 size={18} className="text-orange" /> Your numbers at a glance</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {breakdown.length >= 2 && (
          <div className={chartBox} data-testid="chart-profit-breakdown">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Profit breakdown</p>
            <ResponsiveContainer width="100%" height={breakdown.length * 42}>
              <BarChart layout="vertical" data={breakdown} margin={{ left: 10, right: 60, top: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={82} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                <Tooltip {...tip} cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={16}>
                  {breakdown.map((d, i) => <Cell key={i} fill={d.value < 0 ? "#ef4444" : d.fill} />)}
                  <LabelList dataKey="value" position="right" formatter={(val) => fmtCurrency(val)} style={{ fontSize: 10, fontWeight: 700, fill: "#0f172a" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {beData && (
          <div className={chartBox} data-testid="chart-breakeven">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Break-even headroom</p>
            <ResponsiveContainer width="100%" height={110}>
              <BarChart layout="vertical" data={beData} margin={{ left: 10, right: 60, top: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={82} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                <Tooltip {...tip} cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={16}>
                  {beData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  <LabelList dataKey="value" position="right" formatter={(val) => fmtCurrency(val)} style={{ fontSize: 10, fontWeight: 700, fill: "#0f172a" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className={`text-sm font-semibold mt-1 ${headroom >= 0 ? "text-teal" : "text-red-500"}`}>
              {headroom >= 0 ? `${fmtCurrency(headroom)} above break-even` : `${fmtCurrency(-headroom)} below break-even`}
            </p>
          </div>
        )}
        {conc && (
          <div className={chartBox} data-testid="chart-concentration">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Customer concentration</p>
            <div className="flex items-center gap-4">
              <div className="relative" style={{ width: 130, height: 130 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={conc} dataKey="value" innerRadius={42} outerRadius={62} paddingAngle={2} stroke="none">
                      {conc.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Pie>
                    <Tooltip {...tip} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="font-display font-extrabold text-xl text-navy">{concPct}%</span>
                  <span className="text-[9px] text-slate-400">largest</span>
                </div>
              </div>
              <div className="text-sm space-y-2">
                <p className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-orange inline-block" /> Largest <strong className="text-navy">{fmtCurrency(conc[0].value)}</strong></p>
                <p className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-navy inline-block" /> All others <strong className="text-navy">{fmtCurrency(conc[1].value)}</strong></p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
const chartBox = "bg-white rounded-2xl border border-slate-200 shadow-sm p-5";

const box = "bg-white rounded-2xl border border-slate-200 shadow-sm p-5";
const num = "w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-orange/40";
const pill = (active) => `text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${active ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;

// ─────────────────────────── What-if modelling ───────────────────────────
export function WhatIf({ results, cfg, onScenarios }) {
  const v = results._inputs || {};
  const days = results._period?.days || 365;
  const [lines, setLines] = useState({});
  const emit = (key, text) => setLines((l) => { const next = { ...l }; if (text) next[key] = text; else delete next[key]; return next; });
  useEffect(() => { onScenarios(Object.values(lines)); }, [lines]); // eslint-disable-line

  const gmOk = results.gross_margin?.status === "ok" && v.turnover;
  const ddOk = results.debtor_days?.status === "ok" && v.turnover;
  const beOk = results.breakeven_annual?.status === "ok" && v.turnover;
  const ccOk = results.customer_concentration?.status === "ok" && v.turnover;
  const payOk = v.turnover && ((v.direct_payroll || 0) + (v.overhead_payroll || 0)) > 0;
  if (!gmOk && !ddOk && !beOk && !ccOk && !payOk) return null;

  return (
    <div data-testid="calc-whatif">
      <h2 className="font-display font-extrabold text-navy text-lg flex items-center gap-2 mb-4"><Sliders size={18} className="text-orange" /> {cfg.whatif_heading}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {gmOk && <GrossMarginScenario v={v} current={results.gross_margin.value} emit={emit} />}
        {ddOk && <DebtorDaysScenario v={v} current={results.debtor_days.value} days={days} emit={emit} />}
        {beOk && <BreakevenScenario v={v} be={results.breakeven_annual.value} emit={emit} />}
        {ccOk && <ConcentrationScenario v={v} be={beOk ? results.breakeven_annual.value : null} emit={emit} />}
        {payOk && <PayrollScenario v={v} emit={emit} />}
      </div>
    </div>
  );
}

function GrossMarginScenario({ v, current, emit }) {
  const [delta, setDelta] = useState(1);
  const target = current + delta;
  const additional = v.turnover * (target / 100 - current / 100);
  useEffect(() => { emit("gm", `Increasing gross margin from ${current.toFixed(1)}% to ${target.toFixed(1)}% could add about ${fmtCurrency(additional)} of annual gross profit (assuming revenue is unchanged).`); }, [delta]); // eslint-disable-line
  return (
    <div className={box} data-testid="whatif-gross-margin">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Gross margin uplift</p>
      <p className="text-sm text-slate-600 mt-1">Current margin: <strong className="text-navy">{current.toFixed(1)}%</strong></p>
      <div className="flex flex-wrap gap-2 mt-3">
        {[0.5, 1, 2, 3].map((d) => <button key={d} onClick={() => setDelta(d)} className={pill(delta === d)} data-testid={`whatif-gm-${d}`}>+{d} pts</button>)}
      </div>
      <p className="text-sm mt-3 text-slate-700">At <strong className="text-navy">{target.toFixed(1)}%</strong>, potential additional gross profit:</p>
      <p className="font-display font-extrabold text-2xl text-orange mt-0.5" data-testid="whatif-gm-result">{fmtCurrency(additional)}</p>
    </div>
  );
}

function DebtorDaysScenario({ v, current, days, emit }) {
  const [target, setTarget] = useState(Math.max(0, Math.round(current) - 10));
  const released = ((current - target) / days) * v.turnover;
  useEffect(() => { emit("dd", `Reducing debtor days from ${Math.round(current)} to ${target} could release around ${fmtCurrency(Math.max(0, released))} of cash currently tied up in invoices (this releases cash rather than creating profit).`); }, [target]); // eslint-disable-line
  return (
    <div className={box} data-testid="whatif-debtor-days">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Debtor days target</p>
      <p className="text-sm text-slate-600 mt-1">Current: <strong className="text-navy">{Math.round(current)} days</strong></p>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-sm text-slate-500">Target</span>
        <input type="number" value={target} onChange={(e) => setTarget(Math.max(0, parseInt(e.target.value) || 0))} className={num} data-testid="whatif-dd-input" />
        <span className="text-sm text-slate-500">days</span>
      </div>
      <p className="text-sm mt-3 text-slate-700">Potential cash released:</p>
      <p className="font-display font-extrabold text-2xl text-orange mt-0.5" data-testid="whatif-dd-result">{fmtCurrency(Math.max(0, released))}</p>
      <p className="text-[11px] text-slate-400 mt-1">Releasing cash improves your bank balance — it doesn't add to profit.</p>
    </div>
  );
}

function BreakevenScenario({ v, be, emit }) {
  const [drop, setDrop] = useState(10);
  const newRev = v.turnover * (1 - drop / 100);
  const above = newRev - be;
  useEffect(() => { emit("be", `If revenue dropped by ${drop}% to ${fmtCurrency(newRev)}, you would be ${above >= 0 ? "still above" : "below"} your estimated break-even of ${fmtCurrency(be)} (by ${fmtCurrency(Math.abs(above))}).`); }, [drop]); // eslint-disable-line
  return (
    <div className={box} data-testid="whatif-breakeven">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Revenue drop vs break-even</p>
      <p className="text-sm text-slate-600 mt-1">Break-even: <strong className="text-navy">{fmtCurrency(be)}</strong></p>
      <div className="flex flex-wrap gap-2 mt-3">
        {[5, 10, 20].map((d) => <button key={d} onClick={() => setDrop(d)} className={pill(drop === d)} data-testid={`whatif-be-${d}`}>−{d}%</button>)}
      </div>
      <p className="text-sm mt-3 text-slate-700">Revenue at −{drop}%: <strong className="text-navy">{fmtCurrency(newRev)}</strong></p>
      <p className={`font-display font-extrabold text-xl mt-0.5 ${above >= 0 ? "text-teal" : "text-red-500"}`} data-testid="whatif-be-result">
        {above >= 0 ? "Still above break-even" : "Below break-even"} ({fmtCurrency(Math.abs(above))})
      </p>
    </div>
  );
}

function ConcentrationScenario({ v, be, emit }) {
  const largest = v.largest_customer_revenue || 0;
  const remaining = v.turnover - largest;
  const covers = be != null ? remaining >= be : null;
  useEffect(() => { emit("cc", `If your largest customer (${fmtCurrency(largest)}) left, remaining revenue would be ${fmtCurrency(remaining)}${be != null ? `, which would ${covers ? "still cover" : "fall short of"} your estimated break-even of ${fmtCurrency(be)}` : ""}.`); }, []); // eslint-disable-line
  return (
    <div className={box} data-testid="whatif-concentration">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">What if your largest customer left?</p>
      <p className="text-sm text-slate-600 mt-2">Revenue now: <strong className="text-navy">{fmtCurrency(v.turnover)}</strong></p>
      <p className="text-sm text-slate-600">Revenue lost: <strong className="text-navy">{fmtCurrency(largest)}</strong></p>
      <p className="text-sm mt-2 text-slate-700">Remaining revenue:</p>
      <p className="font-display font-extrabold text-2xl text-navy mt-0.5" data-testid="whatif-cc-result">{fmtCurrency(remaining)}</p>
      {be != null && <p className={`text-sm font-semibold mt-1 ${covers ? "text-teal" : "text-red-500"}`}>{covers ? "Would still cover break-even" : "Would fall below break-even"}</p>}
    </div>
  );
}

function PayrollScenario({ v, emit }) {
  const currentPayroll = (v.direct_payroll || 0) + (v.overhead_payroll || 0);
  const pctDec = currentPayroll / v.turnover;
  const [salary, setSalary] = useState(35000);
  const revNeeded = (currentPayroll + salary) / pctDec;
  const uplift = revNeeded - v.turnover;
  useEffect(() => { emit("pay", `To add a role costing ${fmtCurrency(salary)} while keeping payroll at ${(pctDec * 100).toFixed(1)}% of turnover, you'd need about ${fmtCurrency(uplift)} of additional revenue.`); }, [salary]); // eslint-disable-line
  return (
    <div className={box} data-testid="whatif-payroll">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Hiring impact</p>
      <p className="text-sm text-slate-600 mt-1">Payroll is <strong className="text-navy">{(pctDec * 100).toFixed(1)}%</strong> of turnover</p>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-sm text-slate-500">New salary £</span>
        <input type="number" value={salary} onChange={(e) => setSalary(Math.max(0, parseInt(e.target.value) || 0))} className={num} data-testid="whatif-pay-input" />
      </div>
      <p className="text-sm mt-3 text-slate-700">Extra revenue needed to hold payroll %:</p>
      <p className="font-display font-extrabold text-2xl text-orange mt-0.5" data-testid="whatif-pay-result">{fmtCurrency(uplift)}</p>
    </div>
  );
}

// ─────────────────────────── 90-day action plan ───────────────────────────
export function ActionPlan({ attention, cfg, onChange }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const suggested = attention.slice(0, 3).map((a) => ({ title: cfg.actions?.[a.key]?.title || "Priority", action: cfg.actions?.[a.key]?.action || "", target: "90 days" }));
    setRows(suggested.length ? suggested : [{ title: "Priority", action: "", target: "90 days" }]);
  }, [attention]); // eslint-disable-line
  useEffect(() => { onChange(rows); }, [rows]); // eslint-disable-line
  const upd = (i, k, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: val } : row)));
  const add = () => setRows((r) => [...r, { title: "Priority", action: "", target: "90 days" }]);
  const rm = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  return (
    <div className={box} data-testid="calc-action-plan">
      <h2 className="font-display font-extrabold text-navy text-lg flex items-center gap-2"><ListChecks size={18} className="text-orange" /> {cfg.action_heading}</h2>
      <p className="text-xs text-slate-400 mt-1 mb-4">{cfg.action_intro}</p>
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="border border-slate-100 rounded-xl p-3 relative" data-testid={`action-row-${i}`}>
            <button onClick={() => rm(i)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500"><X size={14} /></button>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <input value={row.title} onChange={(e) => upd(i, "title", e.target.value)} className="text-sm font-semibold text-navy border border-slate-200 rounded-lg px-2.5 py-1.5 w-44" data-testid={`action-title-${i}`} />
              <span className="text-xs text-slate-400">target</span>
              <input value={row.target} onChange={(e) => upd(i, "target", e.target.value)} className="text-xs text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1.5 w-28" data-testid={`action-target-${i}`} />
            </div>
            <textarea value={row.action} onChange={(e) => upd(i, "action", e.target.value)} rows={2} className="w-full text-sm text-slate-700 border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid={`action-text-${i}`} />
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-3 text-sm font-semibold text-teal inline-flex items-center gap-1" data-testid="action-add"><Plus size={14} /> Add a priority</button>
    </div>
  );
}

// ─────────────────────────── Lead capture modal ───────────────────────────
export function LeadModal({ open, onClose, cfg, buildSnapshot }) {
  const [f, setF] = useState({ name: "", last_name: "", business_name: "", email: "", phone: "", marketing_consent: false, report_consent: true, company_website: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, val) => setF((s) => ({ ...s, [k]: val }));

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim()) { toast.error("Please add your name and email"); return; }
    if (!f.report_consent) { toast.error("Please agree to receive your report"); return; }
    setBusy(true);
    try {
      const snap = buildSnapshot();
      const blob = await publicApi.calculatorReport({ ...f, ...snap });
      trackEvent("calculator_lead");
      publicApi.trackCalculator("report_download");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "cass-business-numbers-report.pdf"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report ready — we've also emailed you a copy");
      onClose();
    } catch (e) {
      toast.error("Sorry, we couldn't generate your report. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="calc-lead-modal">
        <DialogHeader><DialogTitle className="font-display">{cfg.lead_heading}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">{cfg.lead_body}</p>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="First name" value={f.name} onChange={(e) => set("name", e.target.value)} className={leadInput} data-testid="lead-name" />
            <input placeholder="Last name" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className={leadInput} data-testid="lead-last-name" />
          </div>
          <input placeholder="Business name" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} className={leadInput} data-testid="lead-business" />
          <input placeholder="Email address" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={leadInput} data-testid="lead-email" />
          <input placeholder="Telephone (optional)" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={leadInput} data-testid="lead-phone" />
          <input type="text" tabIndex={-1} autoComplete="off" value={f.company_website} onChange={(e) => set("company_website", e.target.value)} className="hidden" aria-hidden="true" />
          <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={f.report_consent} onChange={(e) => set("report_consent", e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange" data-testid="lead-report-consent" />
            <span>Yes, email me my personalised Business Numbers Report (PDF).</span>
          </label>
          <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={f.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange" data-testid="lead-marketing-consent" />
            <span>I'd also like occasional practical business tips from CASS. You can unsubscribe any time.</span>
          </label>
          <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold py-3 rounded-full transition-colors" data-testid="lead-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} {busy ? "Preparing…" : "Get my report"}
          </button>
          <p className="text-[11px] text-slate-400 text-center">Your figures are only used to produce your report. We never share them.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const leadInput = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-orange/40";
